import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { ListOrderedIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form/form";
import { Modal } from "@/components/base/modal";
import { OrderingList } from "@/components/base/ordering-list";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";

export function ReorderVenuesForm() {
  const [isOpen, setOpen] = useState(false);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
      <Button
        variant="icon"
        color="secondary"
        tooltip="Reorder Active Locations"
      >
        <ListOrderedIcon size={16} />
      </Button>

      <Modal size="md" isDismissable isOpen={isOpen} onOpenChange={setOpen}>
        {isOpen && <ReorderContent onClose={() => setOpen(false)} />}
      </Modal>
    </DialogTrigger>
  );
}

function ReorderContent({ onClose }: { onClose: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: setVenueOrder } = useMutation({
    ...trpc.venues.reorder.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.venues.pathFilter());
    },
  });

  const { data } = useSuspenseQuery(trpc.venues.adminList.queryOptions());

  const items = useMemo(
    () =>
      data
        .filter((venue) => venue.status === "active")
        .map(({ id, name, city }) => ({
          value: id,
          display: `${name}, ${city}`,
        })),
    [data],
  );

  const form = useAppForm({
    defaultValues: {
      order: items.map(({ value }) => value),
    },
    onSubmit: async ({ value: { order }, formApi }) => {
      await setVenueOrder({ order });
      formApi.reset();
      onClose();
    },
  });

  return (
    <div className="flex flex-col space-y-4 p-4">
      <Heading className={title({ size: "sm" })} slot="title">
        Reorder Active Locations
      </Heading>

      <form
        className="flex-col space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.AppField name="order">
          {(field) => (
            <OrderingList
              items={items}
              onChange={(order) => field.handleChange(order)}
            />
          )}
        </form.AppField>
        <form.AppForm>
          <form.Footer>
            <Button onPress={onClose}>Cancel</Button>

            <form.SubmitButton requireChange={false}>
              Save Order
            </form.SubmitButton>
          </form.Footer>
        </form.AppForm>
      </form>
    </div>
  );
}
